import { useEffect, useState } from "react";
import { Plus, Trash, User, PencilSimple, Check, X } from "@phosphor-icons/react";
import { toast } from "sonner";
import api, { formatApiError, getWithRetry } from "../../lib/api";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import ConfirmDialog from "../ConfirmDialog";

export default function PlayersPanel() {
  const [players, setPlayers] = useState([]);
  const [name, setName] = useState("");
  const [loading, setLoading] = useState(true);
  const [editingId, setEditingId] = useState(null);
  const [editName, setEditName] = useState("");
  const [confirm, setConfirm] = useState(null);

  const load = () => getWithRetry("/players").then((r) => setPlayers(r.data)).catch(() => toast.error("No se pudieron cargar los jugadores. Reintenta.")).finally(() => setLoading(false));
  useEffect(() => { load(); }, []);

  const add = async (e) => {
    e.preventDefault();
    if (!name.trim()) return;
    try {
      await api.post("/players", { name: name.trim() });
      setName(""); load();
      toast.success("Jugador agregado");
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  const startEdit = (p) => { setEditingId(p.id); setEditName(p.name); };
  const cancelEdit = () => { setEditingId(null); setEditName(""); };
  const saveEdit = async (id) => {
    if (!editName.trim()) return;
    try {
      await api.put(`/players/${id}`, { name: editName.trim() });
      cancelEdit(); load();
      toast.success("Jugador actualizado");
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  const remove = async (id) => { await api.delete(`/players/${id}`); load(); toast.success("Jugador eliminado"); };

  return (
    <div>
      <h2 className="font-heading text-3xl font-extrabold uppercase mb-6">Jugadores</h2>
      <form onSubmit={add} className="flex gap-3 mb-8 max-w-md">
        <Input data-testid="player-name-input" placeholder="Nombre del jugador" value={name} onChange={(e) => setName(e.target.value)} className="bg-[#0A0A0C] border-white/15" />
        <Button data-testid="add-player-btn" type="submit" className="bg-padel-blue text-black font-heading uppercase tracking-widest rounded-none hover:bg-padel-green shrink-0">
          <Plus size={18} className="mr-1" /> Agregar
        </Button>
      </form>

      {loading ? (
        <p className="text-padel-blue font-heading uppercase tracking-widest animate-pulse-glow">Cargando jugadores…</p>
      ) : players.length === 0 ? (
        <p className="text-muted-foreground">Sin jugadores todavía.</p>
      ) : (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
          {players.map((p) => (
            <div key={p.id} data-testid={`player-row-${p.id}`} className="bg-[#131417] border border-white/10 px-4 h-14 flex items-center justify-between gap-2">
              {editingId === p.id ? (
                <>
                  <Input
                    data-testid={`edit-player-input-${p.id}`}
                    autoFocus
                    value={editName}
                    onChange={(e) => setEditName(e.target.value)}
                    onKeyDown={(e) => { if (e.key === "Enter") saveEdit(p.id); if (e.key === "Escape") cancelEdit(); }}
                    className="bg-[#0A0A0C] border-white/15 h-9"
                  />
                  <div className="flex gap-1 shrink-0">
                    <button data-testid={`save-player-${p.id}`} onClick={() => saveEdit(p.id)} className="text-padel-green hover:text-padel-green/80"><Check size={20} weight="bold" /></button>
                    <button data-testid={`cancel-player-${p.id}`} onClick={cancelEdit} className="text-muted-foreground hover:text-white"><X size={20} /></button>
                  </div>
                </>
              ) : (
                <>
                  <span className="flex items-center gap-3 font-medium min-w-0 truncate"><User size={20} weight="duotone" className="text-padel-blue shrink-0" /><span className="truncate">{p.name}</span></span>
                  <div className="flex gap-1 shrink-0">
                    <button data-testid={`edit-player-${p.id}`} onClick={() => startEdit(p)} className="text-muted-foreground hover:text-padel-blue"><PencilSimple size={18} /></button>
                    <button data-testid={`delete-player-${p.id}`} onClick={() => setConfirm(p)} className="text-muted-foreground hover:text-padel-danger"><Trash size={18} /></button>
                  </div>
                </>
              )}
            </div>
          ))}
        </div>
      )}

      <ConfirmDialog
        open={!!confirm}
        onOpenChange={(o) => !o && setConfirm(null)}
        title="Eliminar jugador"
        description={confirm ? `¿Seguro que quieres eliminar a "${confirm.name}"? Esta acción no se puede deshacer.` : ""}
        onConfirm={() => confirm && remove(confirm.id)}
      />
    </div>
  );
}
